import { syncAuthSessionOnUnauthorizedResponse } from '@/infrastructure/auth/unauthorized-session-bridge';
import { API_BASE_URL, DEFAULT_PLATFORM_ADMIN_LOCALE } from '@/shared/constants';
import type { ApiResponse } from '@/shared/types';

import { ApiHttpError, ApiTransportError, extractErrorMessage } from './error';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Hard ceiling for any single request. The admin gateway lives behind a
 * remote office link, so a degraded (not severed) connection can leave a
 * `fetch` hanging indefinitely with no UI recovery. Aborting client-side
 * guarantees the caller's `onError` runs and loading state is released,
 * instead of depending on the platform's request timeout.
 */
export const REQUEST_TIMEOUT_MS = 25_000;

type DefaultHeadersProvider = () => Record<string, string>;

interface RequestOptions<TBody = unknown> {
  method?: HttpMethod;
  body?: TBody;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
}

class ApiClient {
  private baseUrl: string;
  private defaultHeadersProvider: DefaultHeadersProvider = () => ({});

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Register a provider that returns headers to merge into every request.
   * Used at app bootstrap to inject cross-cutting concerns (e.g. `X-Locale`)
   * without coupling feature code to the global session/locale state.
   */
  setDefaultHeadersProvider(provider: DefaultHeadersProvider): void {
    this.defaultHeadersProvider = provider;
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    const base = this.baseUrl.endsWith('/')
      ? this.baseUrl.slice(0, -1)
      : this.baseUrl;
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const fullUrl = `${base}${path}`;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
      const qs = searchParams.toString();
      return qs ? `${fullUrl}?${qs}` : fullUrl;
    }

    return fullUrl;
  }

  async request<TResponse, TBody = unknown>(
    endpoint: string,
    options: RequestOptions<TBody> = {}
  ): Promise<ApiResponse<TResponse>> {
    const { method = 'GET', body, headers = {}, params } = options;

    const url = this.buildUrl(endpoint, params);

    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

    const finalHeaders: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...this.defaultHeadersProvider(),
      ...headers,
    };

    const hasBody = body !== undefined && body !== null;
    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers: finalHeaders,
        body: hasBody
          ? isFormData
            ? (body as unknown as FormData)
            : JSON.stringify(body)
          : undefined,
        credentials: 'include',
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
    } catch (error) {
      // Translate transport-level failures (timeout, offline, DNS) into a
      // typed error with a human-readable message. Without this the caller
      // surfaces the raw "Failed to fetch" / "signal timed out", which reads
      // as a bug to users. `ApiTransportError` keeps retry classifiers
      // working (transient vs HTTP error) and preserves the original
      // rejection as `cause` for diagnostics.
      if (error instanceof DOMException && error.name === 'TimeoutError') {
        throw new ApiTransportError(
          'timeout',
          'Request timed out. Please check your connection and try again.',
          error,
        );
      }
      // Browser fetch rejects with a TypeError on network-level failures
      // (offline, DNS, connection reset, CORS).
      if (error instanceof TypeError) {
        throw new ApiTransportError(
          'network',
          'Network error. Please check your connection and try again.',
          error,
        );
      }
      // Anything else (e.g. a serialization/programming bug) is not a
      // connectivity issue: rethrow untouched so it stays diagnosable.
      throw error;
    }

    if (response.status === 401) {
      syncAuthSessionOnUnauthorizedResponse(401);
    }

    const contentType = response.headers.get('content-type');
    const hasJsonContent = contentType?.includes('application/json') ?? false;
    const isEmptyResponse = response.status === 204 || response.status === 205;

    if (!response.ok) {
      let errorData: unknown = {};
      if (hasJsonContent && !isEmptyResponse) {
        errorData = await response.json().catch(() => ({}));
      }
      throw new ApiHttpError(
        response.status,
        extractErrorMessage(errorData, `HTTP error! status: ${response.status}`),
        errorData,
      );
    }

    let data: TResponse | null = null;
    if (!isEmptyResponse && hasJsonContent) {
      try {
        data = await response.json();
      } catch {
        data = null;
      }
    }

    return {
      data: data as TResponse,
      success: true,
    };
  }

  async get<TResponse>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
    headers?: Record<string, string>
  ): Promise<ApiResponse<TResponse>> {
    return this.request<TResponse>(endpoint, { method: 'GET', params, headers });
  }

  async post<TResponse, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    headers?: Record<string, string>
  ): Promise<ApiResponse<TResponse>> {
    return this.request<TResponse, TBody>(endpoint, { method: 'POST', body, headers });
  }

  async put<TResponse, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    headers?: Record<string, string>
  ): Promise<ApiResponse<TResponse>> {
    return this.request<TResponse, TBody>(endpoint, { method: 'PUT', body, headers });
  }

  async patch<TResponse, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    headers?: Record<string, string>
  ): Promise<ApiResponse<TResponse>> {
    return this.request<TResponse, TBody>(endpoint, { method: 'PATCH', body, headers });
  }

  async delete<TResponse, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    headers?: Record<string, string>,
  ): Promise<ApiResponse<TResponse>> {
    return this.request<TResponse, TBody>(endpoint, { method: 'DELETE', body, headers });
  }
}

/** Routes through `/api/admin/*` BFF — admin-api-gateway. */
export const apiClient = new ApiClient(API_BASE_URL);

/**
 * Routes through `/api/*` BFF (auth + admin proxies that live outside
 * `/api/admin/*`), e.g. `/api/users/*`, `/api/invitations/*`, `/api/me/*`.
 * Use this for endpoints proxied by feature-specific BFF route handlers.
 */
export const bffClient = new ApiClient('/api');

/**
 * Platform-admin currently runs in English. The locale hint is sent on
 * every request so the gateway can translate enum labels (roles, audit
 * types, etc.) consistently. When a user-driven locale lands, this getter
 * can be replaced with a session/preferences accessor without touching
 * any feature code.
 */
const PLATFORM_ADMIN_DEFAULT_HEADERS: Record<string, string> = {
  'X-Locale': DEFAULT_PLATFORM_ADMIN_LOCALE,
};

apiClient.setDefaultHeadersProvider(() => PLATFORM_ADMIN_DEFAULT_HEADERS);
bffClient.setDefaultHeadersProvider(() => PLATFORM_ADMIN_DEFAULT_HEADERS);
